package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

/**
 * The `<label>` is the control, so the `<label>` is what has to meet the hit floor.
 *
 * **The real `<input>` is 1x1 and invisible** — see [visuallyHiddenChoiceModifier] — so everything
 * a person clicks is this label and the styled box beside it. The box is 18px and the switch track
 * is 24, and neither the label nor either of them carried a height, so the whole target was as tall
 * as one line of `body/15`: about 22px for a checkbox, 24 for a switch, against a floor of
 * 40 that the spec states numerically and exempts nothing from.
 *
 * `scripts/check-control-geometry.mjs` could not have caught it and still cannot without a change
 * of its own: its selector lists `input` and `[role="switch"]`, and it then discards any box under
 * 2x2 as "a visually-hidden input behind a styled control". Both halves are individually correct
 * and together they filter this control out completely — the input is dropped for being 1x1, and
 * the label that receives the click is never queried. Fixing the geometry without saying so would
 * leave the next regression just as invisible.
 *
 * `minHeight`, not `height`: a long label that wraps has to be allowed to grow.
 */
val LCheckboxStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textPrimary)
            .fontSize(TypeScale.BodySmall)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "8px")
            }
    }
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
    cssRule(" input:focus-visible + span") {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val LCheckboxBoxStyle = CssStyle.base {
    Modifier
        .position(Position.Relative)
        .width(18.px)
        .height(18.px)
        // **4px, not `Radius.Control`.** The 8px control token was calibrated for 32-40px buttons
        // and fields; on an 18px box it leaves 2px of straight edge per side, which at 1x is a
        // circle — the universal "pick one" affordance, on the control that drives multi-select and
        // the bulk action bar. The switch beside it keeps `Radius.Pill`; the token itself is right
        // where it is used at the size it was drawn for.
        .borderRadius(4.px)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("flex", "0 0 auto")
        }
        .transitionColors()
}

/**
 * The dash a checkbox draws when it is neither on nor off.
 *
 * A select-all header over a partly-selected table is `mixed`, and the only honest paint for it is
 * a bar — a tick would claim every row is selected and an empty box would claim none is. It is a
 * `<span>` rather than a glyph because the pinned Lucide set has no `minus`, and a 2px rule is not
 * an icon: nothing about it would improve by being one.
 *
 * Mounted whether or not the control is mixed, and faded with `opacity`, for the reason the tick
 * beside it is: a node that unmounts on every state change cannot be transitioned, and
 * `DesignSystemInteractionTest` pins that contract on the tick already.
 */
val LCheckboxDashStyle = CssStyle.base {
    Modifier
        .width(10.px)
        .height(2.px)
        .borderRadius(1.px)
        .styleModifier { property("flex", "0 0 auto") }
        .transitionSurface(durationMs = Motion.FastMs)
}

/** Same floor, same reason as [LCheckboxStyle]: the switch's target is its label, not its track. */
val LSwitchStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textPrimary)
            .fontSize(TypeScale.BodySmall)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "8px")
            }
    }
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
    cssRule(" input:focus-visible + span") {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val LSwitchTrackStyle = CssStyle.base {
    Modifier
        .position(Position.Relative)
        .width(40.px)
        .height(24.px)
        .borderRadius(Radius.Pill)
        .styleModifier {
            property("display", "inline-block")
            property("flex", "0 0 auto")
        }
        .transitionColors()
}

val LSwitchThumbStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .width(14.px)
        .height(14.px)
        .backgroundColor(colorMode.toSitePalette().surface)
        .borderRadius(Radius.Pill)
        .styleModifier {
            property("top", "4px")
            // The edge the thumb rests against, named logically. Its travel is in
            // `switchThumbTransform`, which multiplies by `--l-inline-direction` so the two agree:
            // pinning one and not the other slides the thumb off the track in Arabic.
            property("inset-inline-start", "4px")
        }
        .transition("transform", durationMs = Motion.StandardMs, easing = Motion.EaseInOut)
}

@Composable
fun LCheckbox(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    /**
     * Keeps [label] for assistive technology while removing it from the page.
     *
     * For a checkbox in a table cell, where the column heading already says what the control is and
     * repeating it on every row would be noise. **This exists because the obvious way to get it is
     * wrong**: passing `LVisuallyHiddenStyle` as [modifier] hides the root, and the root is the
     * `label` element that contains the box — so the whole control disappears and the column becomes
     * an empty cell nobody can click. That is what the keys table shipped with.
     */
    labelHidden: Boolean = false,
    /**
     * Neither on nor off — the third state a select-all control genuinely has.
     *
     * `indeterminate` is an IDL property and **not an attribute**: there is no `indeterminate=""` to
     * write, so it is set through `prop`, which reapplies it on every recomposition. A `ref` would
     * have set it once, on mount, and then never again — which is precisely wrong for a header
     * checkbox whose state changes every time a row is ticked.
     *
     * [checked] still governs the underlying box, so a mixed control is `checked = false`: a mixed
     * checkbox that is also checked announces "checked" and hands a reader the wrong answer to the
     * only question they asked. `aria-checked="mixed"` is set alongside the property so the state
     * survives a platform that does not map the IDL property.
     */
    indeterminate: Boolean = false,
) {
    val palette = ColorMode.current.toSitePalette()
    val filled = checked && !indeterminate
    val marked = filled || indeterminate
    Label(
        attrs = LCheckboxStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("data-disabled", (!enabled).toString())
                attr("data-state", choiceState(checked, indeterminate))
            },
    ) {
        Input(
            type = InputType.Checkbox,
            attrs = visuallyHiddenChoiceModifier().toAttrs {
                checked(filled)
                if (!enabled) disabled()
                if (indeterminate) attr("aria-checked", "mixed")
                prop({ element: HTMLInputElement, value: Boolean -> element.indeterminate = value }, indeterminate)
                onChange { onCheckedChange(it.value) }
            },
        )
        Span(
            LCheckboxBoxStyle.toModifier()
                .backgroundColor(if (marked) palette.accent else palette.surfaceSunken)
                .border(
                    1.px,
                    LineStyle.Solid,
                    if (marked) palette.accent else palette.borderStrong,
                )
                .color(palette.accentContrast)
                .toAttrs {
                    attr("data-checkbox-box", "")
                    attr("data-state", choiceState(checked, indeterminate))
                    attr("aria-hidden", "true")
                },
        ) {
            Span(
                Modifier
                    .iconSize(IconSize.SM)
                    .styleModifier {
                        property("display", "inline-flex")
                        property("opacity", if (filled) "1" else "0")
                        property("transform", checkboxGlyphTransform(filled))
                    }
                    .transitionSurface(durationMs = Motion.FastMs)
                    .toAttrs {
                        attr("data-checkbox-glyph", "")
                        attr("data-state", choiceState(checked, indeterminate))
                    },
            ) {
                LCheckIcon(Modifier.iconSize(IconSize.SM))
            }
            Span(
                LCheckboxDashStyle.toModifier()
                    .backgroundColor(palette.accentContrast)
                    .styleModifier {
                        property("opacity", if (indeterminate) "1" else "0")
                        // The dash and the tick occupy the same 18px box, so the dash is taken out
                        // of flow rather than laid out beside a tick that is only invisible.
                        property("position", "absolute")
                    }
                    .toAttrs {
                        attr("data-checkbox-dash", "")
                        attr("data-state", choiceState(checked, indeterminate))
                    },
            )
        }
        if (labelHidden) {
            Span(LVisuallyHiddenStyle.toModifier().toAttrs()) { Text(label) }
        } else {
            Text(label)
        }
    }
}

@Composable
fun LSwitch(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    val palette = ColorMode.current.toSitePalette()
    var pressed by remember { mutableStateOf(false) }
    Label(
        attrs = LSwitchStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("data-disabled", (!enabled).toString())
                attr("data-state", choiceState(checked))
                onMouseDown { if (enabled) pressed = true }
                onMouseUp { pressed = false }
                onMouseLeave { pressed = false }
                onTouchStart { if (enabled) pressed = true }
                onTouchEnd { pressed = false }
                onTouchCancel { pressed = false }
                onKeyDown { event ->
                    if (enabled && event.key in SWITCH_ACTIVATION_KEYS) pressed = true
                }
                onKeyUp { event ->
                    if (event.key in SWITCH_ACTIVATION_KEYS) pressed = false
                }
                onBlur { pressed = false }
            },
    ) {
        Input(
            type = InputType.Checkbox,
            attrs = visuallyHiddenChoiceModifier().toAttrs {
                checked(checked)
                if (!enabled) disabled()
                attr("role", "switch")
                attr("aria-checked", checked.toString())
                onChange { onCheckedChange(it.value) }
            },
        )
        Span(
            LSwitchTrackStyle.toModifier()
                .backgroundColor(if (checked) palette.accent else palette.surfaceSunken)
                .border(
                    1.px,
                    LineStyle.Solid,
                    if (checked) palette.accent else palette.borderStrong,
                )
                .toAttrs {
                    attr("data-switch-track", "")
                    attr("data-state", choiceState(checked))
                    attr("aria-hidden", "true")
                },
        ) {
            Span(
                LSwitchThumbStyle.toModifier()
                    .styleModifier {
                        property("transform", switchThumbTransform(checked = checked, pressed = pressed))
                        property(
                            "transition-duration",
                            "${if (pressed) Motion.FastMs else Motion.StandardMs}ms",
                        )
                    }
                    .toAttrs {
                        attr("data-switch-thumb", "")
                        attr("data-pressed", pressed.toString())
                    },
            )
        }
        Text(label)
    }
}

private fun visuallyHiddenChoiceModifier(): Modifier = Modifier
    .position(Position.Absolute)
    .width(1.px)
    .height(1.px)
    .styleModifier {
        property("opacity", "0")
        property("pointer-events", "none")
    }

internal fun checkboxGlyphTransform(checked: Boolean): String =
    if (checked) "scale(1)" else "scale(0.92)"

internal fun switchThumbTransform(checked: Boolean, pressed: Boolean): String {
    val translation = if (checked) 16 else 0
    val scale = if (pressed) Motion.PressScale.toString() else "1"
    // `translateX` is physical and there is no logical spelling of it, so the sign comes from
    // `--l-inline-direction`, which the theme flips under `[dir="rtl"]`. The fallback of 1 keeps
    // this correct anywhere the variable has not been registered, a test harness included.
    // `scale`, not `scaleX`. The thumb is a 14x14 circle, so compressing one axis pressed it into
    // an ellipse — a shape change rather than the pressure the press scale is meant to read as.
    return "translateX(calc(${translation}px * var(--l-inline-direction, 1))) scale($scale)"
}

private val SWITCH_ACTIVATION_KEYS = setOf(" ", "Enter")

private fun choiceState(checked: Boolean): String = if (checked) "checked" else "unchecked"

/**
 * `data-state` for a control that has three of them.
 *
 * Mixed wins over checked because a mixed control is drawn as mixed; the two-argument form stays
 * for the switch, which cannot be mixed at all.
 */
private fun choiceState(checked: Boolean, indeterminate: Boolean): String =
    if (indeterminate) "indeterminate" else choiceState(checked)
